import React from 'react';
import { useHistory, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Skeleton from "react-loading-skeleton";
import BannerDisplay from "./BannerDisplay";
import FileUpload from "../../../components/FileUpload";
import {COUNTRY_OPTIONS, getGradeName} from "../../../constants";
import {getOptionName} from "../../../utils/getOptionName";
import PreImage from "../../../components/PreImage";
import FeatherIcon from "feather-icons-react";
import { useTheme } from "../../../contexts/ThemeContext";

const TournamentInformation = ({ tournament, loading }) => {
    const { t } = useTranslation();
    const history = useHistory();
    const routerLocation = useLocation();
    const { compactTable } = useTheme();

    if (!tournament && !loading) return null;

    const data = tournament || {};

    // same "open the club's own page" link the tournament header gives -
    // surfaced here too so a public visitor reading the info table can jump
    // straight to the club instead of hunting for it in the clubs list.
    const openClubModal = (e) => {
        e.preventDefault();
        if (!data.club) return;
        const params = new URLSearchParams(routerLocation.search);
        params.set("club_id", data.club);
        // replace, not push - same reasoning as TournamentHeader.js's own
        // openClubModal (see its comment): a push here left an extra
        // history entry the tournament header's back-arrow goBack() would
        // undo first.
        history.replace({ search: params.toString() });
    };

    // Locations don't have their own in-app profile page/modal (unlike
    // clubs) - a genuine external link (Google Maps) is the only "go look
    // at this place" option available.
    const locationMapsQuery = [
        data.location_name,
        data.city,
        data.country ? t(getOptionName(COUNTRY_OPTIONS, data.country)) : null
    ].filter(Boolean).join(', ');
    const locationMapsUrl = locationMapsQuery
        ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(locationMapsQuery)}`
        : null;

    // location_image (the venue/base's own stock photo) fills in for a
    // tournament that hasn't uploaded its own banner - reads fine in the
    // full card layout, but in Compact table mode showing someone else's
    // location photo as if it were this tournament's own banner reads as
    // misleading rather than helpful, so it's skipped there: no banner
    // means no image at all, not a substitute one.
    const hasOwnImage = !!data.image;
    const showLocationFallback = !compactTable && !hasOwnImage && !!data.location_image;
    const bannerSrc = data.image?.url || data.image || (showLocationFallback ? data.location_image : null);
    const bannerWidth = hasOwnImage ? data.width : (showLocationFallback ? data.location_width : data.width);
    const bannerHeight = hasOwnImage ? data.height : (showLocationFallback ? data.location_height : data.height);

    return (
        <div id="information-block" className="tournament-form" style={{ marginTop: "15px" }}>
            <div className="item-container no-cursor">
                <div style={{position: "relative", marginBottom: "10px"}} className="no-laptop">
                    {
                        bannerSrc ?
                            <PreImage
                                src={bannerSrc}
                                width={bannerWidth}
                                height={bannerHeight}
                            />
                            :
                                <div className="banner-preview non-authorized" style={{minHeight: "200px"}}>
                                    <FeatherIcon icon="camera-off" />
                                </div>
                    }
                </div>
                <table className="tournament_table">
                    <tbody>
                    <tr>
                        <td>{t("Club")}</td>
                        <td>
                            {loading
                                ? <Skeleton height={42} width="100%" count={1} baseColor="#f8f7fa" />
                                : data?.club_name
                                    ? (
                                        <a href="#" onClick={openClubModal} title={t("Club page")}>
                                            {data.club_name}
                                            <FeatherIcon icon="external-link" size={12} style={{ marginLeft: "6px" }} />
                                        </a>
                                    )
                                    : "—"
                            }
                        </td>
                    </tr>
                    <tr>
                        <td>{t("Name")}</td>
                        <td>
                            {loading
                                ? <Skeleton height={42} width="100%" count={1} baseColor="#f8f7fa" />
                                : data?.name || "—"
                            }
                        </td>
                    </tr>
                    <tr>
                        <td>{t("Start Date")}</td>
                        <td>
                            {loading ? (
                                <Skeleton height={42} width={'100%'} count={1} baseColor={'#f8f7fa'}/>
                            ) : data.start_date
                                ? new Date(data.start_date).toLocaleString(undefined, {
                                    day: '2-digit',
                                    month: '2-digit',
                                    year: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                })
                                : ''
                            }
                        </td>
                    </tr>
                    <tr>
                        <td>{t("End Date")}</td>
                        <td>
                            {loading ? (
                                <Skeleton height={42} width={'100%'} count={1} baseColor={'#f8f7fa'}/>
                            ) : data.end_date
                                ? new Date(data.end_date).toLocaleString(undefined, {
                                    day: '2-digit',
                                    month: '2-digit',
                                    year: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                })
                                : ''
                            }
                        </td>
                    </tr>
                    <tr>
                        <td>{t("Format")}</td>
                        <td>
                            {loading ? (
                                <Skeleton height={42} width={'100%'} count={1} baseColor={'#f8f7fa'}/>
                            ) :
                                        data.grade
                                            ? `${getGradeName(data.grade)} · ${
                                                data.gender ? t(`gender.${data.gender}`) : ''
                                            }`
                                            : ''
                                    }
                        </td>
                    </tr>
                    <tr>
                        <td>{t("Location")}</td>
                        <td>
                            {loading ? (
                                <Skeleton height={42} width="100%" count={1} baseColor="#f8f7fa" />
                            ) : locationMapsUrl ? (
                                <a href={locationMapsUrl} target="_blank" rel="noopener noreferrer" title={t("Open in Maps")}>
                                    {[data.location_name, data.city, data.country ? t(getOptionName(COUNTRY_OPTIONS, data.country)) : null]
                                        .filter(Boolean)
                                        .join(' · ')}
                                    <FeatherIcon icon="external-link" size={12} style={{ marginLeft: "6px" }} />
                                </a>
                            ) : "—"}
                        </td>
                    </tr>
                    <tr>
                        <td>{t("Level")}</td>
                        <td>
                            {loading ? (
                                <Skeleton height={42} width={'100%'} count={1} baseColor={'#f8f7fa'}/>
                            ) : data.format || ''}
                        </td>
                    </tr>
                    <tr>
                        <td>{t("Age")}</td>
                        <td>
                            {loading ? (
                                <Skeleton height={42} width={'100%'} count={1} baseColor={'#f8f7fa'}/>
                            ) :  data.age || ''
                            }
                        </td>
                    </tr>
                    <tr>
                        <td>{t("Organizers and sponsors")}</td>
                        <td>
                            {loading ? (
                                <Skeleton height={42} width={'100%'} count={1} baseColor={'#f8f7fa'}/>
                            ) : data.participation || ''}
                        </td>
                    </tr>
                    <tr>
                        <td>{t("Drawing")}</td>
                        <td>
                            {loading ? (
                                <Skeleton height={42} width={'100%'} count={1} baseColor={'#f8f7fa'}/>
                            ) : data.sort || ''}
                        </td>
                    </tr>
                    <tr>
                        <td>{t("Head referee")}</td>
                        <td>
                            {loading ? (
                                <Skeleton height={42} width={'100%'} count={1} baseColor={'#f8f7fa'}/>
                            ) : data.tasks || ''}
                        </td>
                    </tr>
                    <tr>
                        <td>{t("Money")}</td>
                        <td>
                            {loading ? (
                                <Skeleton height={42} width={'100%'} count={1} baseColor={'#f8f7fa'}/>
                            ) : data.money || ''}
                        </td>
                    </tr>
                    <tr>
                        <td>{t("Prize Fund")}</td>
                        <td>
                            {loading ? (
                                <Skeleton height={42} width={'100%'} count={1} baseColor={'#f8f7fa'}/>
                            ) : data.prize_fund || ''}
                        </td>
                    </tr>
                    <tr>
                        <td>{t("Official Ball and court surface")}</td>
                        <td>
                            {loading ? (
                                <Skeleton height={42} width={'100%'} count={1} baseColor={'#f8f7fa'}/>
                            ) : data.official_ball || ''}
                        </td>
                    </tr>
                    <tr>
                        <td>{t("Award")}</td>
                        <td>
                            {loading ? (
                                <Skeleton height={42} width={'100%'} count={1} baseColor={'#f8f7fa'}/>
                            ) : data.award || ''}
                        </td>
                    </tr>
                    <tr>
                        <td>{t("Contact data")}</td>
                        <td>
                            {loading ? (
                                <Skeleton height={42} width={'100%'} count={1} baseColor={'#f8f7fa'}/>
                            ) : data.phone || ''}
                        </td>
                    </tr>
                    </tbody>
                </table>
            </div>

            <div className="sidebar-wrapper">
                <div className="no-mobile">
                    {loading ? (
                        <Skeleton height={200} width={'100%'} baseColor={'#f8f7fa'} />
                    ) :
                        <div className="content_page banner-container">
                            <label>{t("Image")}</label>
                            <BannerDisplay
                                image={bannerSrc}
                                width={bannerWidth}
                                height={bannerHeight}
                                is_authorized={false}
                            />
                        </div>
                    }
                </div>
                <div>
                    <FileUpload
                        id={data.id}
                        file={data.file}
                        loading={loading}
                        is_authorized={false}
                    />
                </div>
            </div>
        </div>
    );
};

export default TournamentInformation;
